import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { Controller } from "../interface/controller.interface";
import { HTTPMethod } from "../model/http-method.enum";
import { HTTPRequestBuilder } from "../model/http.request.model";
import { HTTPService } from "./http.service";
import { orElse } from "../model/operator/or-else.operator";
import { Holiday } from "../persistence/dto/holiday.model";
import { TypeSystem } from "../support/type-system.support";

	/**
	 * @description
	 *  Servicio reflejo del controlador de zonas de campañas del backend.
	 *
	 * @see gigot-backend/Controllers/Sales/CampaignZonasController.cs
	 */

@Injectable({ providedIn: "root" })
export class HolidayService implements Controller {

	constructor(
		private httpService: HTTPService) {}

	public allHolidays(): Observable<Holiday[]> {
		return this.httpService.send<typeof undefined, Holiday[]>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath("")
				.build())
			.pipe(
				orElse<Holiday[]>([] as Holiday[]),
				map(holidays => holidays.map(holiday => Holiday.from(holiday))));
	}

	public basePath(): string {
		return "api/holidays";
	}

	public createHoliday(holiday: Holiday): Observable<Holiday> {
		return this.httpService.send<Holiday, Holiday>(
			new HTTPRequestBuilder<Holiday>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath("")
				.payload(holiday)
				.build())
			.pipe(map(holiday => Holiday.from(holiday)));
	}

	public deleteHoliday(id: number): Observable<typeof TypeSystem.EmptyObject> {
        return this.httpService.send<any, null>(
            new HTTPRequestBuilder()
                .method(HTTPMethod.DELETE)
                .controllerPath(this.basePath())
                .relativePath(`${id}`)
                .build())
            .pipe(map(() => TypeSystem.EmptyObject));
    }

	/**
	 * @description
	 *	Busca los días no-laborables disponibles que ocurren en el mismo día,
	 *	mes y año que la fecha especificada; útil para buscar la existencia de
	 *	días no-laborables en el sistema.
	 *
	 * @param date
	 *	La fecha a buscar. El valor del tiempo (horas, minutos, segundos y
	 *	milisegundos), es irrelevante y no interviene en la búsqueda.
	 *
	 * @returns
	 *	Un observable sobre un vector con todos los días no-laborables cuya
	 *	fecha coincide con la especificada, o un vector vacío si no hubo
	 *	coincidencia.
	 */
	public findHolidaysByDate(date: Date): Observable<Holiday[]> {
		const dateString: string = date.toLocaleDateString();
		return this.allHolidays()
			.pipe(map(holidays => holidays
				.filter(holiday => dateString === holiday.date.toLocaleDateString())))
	}

	public updateHoliday(holiday: Holiday): Observable<typeof TypeSystem.EmptyObject> {
		return this.httpService.send<Holiday, null>(
			new HTTPRequestBuilder<Holiday>()
				.method(HTTPMethod.PUT)
				.controllerPath(this.basePath())
				.relativePath(`${holiday.id}`)
				.payload(holiday)
				.build())
			.pipe(map(() => TypeSystem.EmptyObject));
	}
}
